import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { pushChat } from './utils/chatStore';

// Display role string from a piece, matching AdminDashboard.assignRoles
// (e.g. "White King (E1)"). Drives both the label and the vote weight.
const roleFromPiece = (piece) => {
  const typeName = piece.type.charAt(0).toUpperCase() + piece.type.slice(1);
  const colorName = piece.color.charAt(0).toUpperCase() + piece.color.slice(1);
  return `${colorName} ${typeName} (${(piece.startPosition || piece.currentPosition || '').toUpperCase()})`;
};

// Shuffle an array so (ideally) no element keeps its original index — a
// derangement, so every shuffled player genuinely swaps pieces. Falls back to a
// plain shuffle if a derangement isn't found quickly.
const derangedShuffle = (arr) => {
  if (arr.length < 2) return [...arr];
  let out = [...arr];
  for (let attempt = 0; attempt < 20; attempt++) {
    out = [...arr];
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    if (out.every((v, i) => v !== arr[i])) return out;
  }
  return out;
};

// Admin panel: team rosters, current suggestions, vote breakdown, pause toggle.
const AdminPanel = ({ game }) => {
  const [busy, setBusy] = useState(false);

  const togglePause = async () => {
    if (busy || !game) return;
    setBusy(true);
    try {
      const next = !game.paused;
      const update = { paused: next };
      // Pausing freezes humans too: clear the turn timers so nothing expires
      // while paused. On resume the client re-inits a fresh timer for the turn.
      if (next) {
        update['turnTimer.white'] = null;
        update['turnTimer.black'] = null;
      }
      await updateDoc(doc(db, 'games', game.id), update);
    } catch (err) {
      console.error('Failed to toggle pause:', err);
    } finally {
      setBusy(false);
    }
  };

  // Reassign which living piece each active player controls — keeping their
  // identity/personality, only swapping the piece (and so the role + vote
  // weight). The board itself is untouched; this is purely a change of
  // ownership. Pauses the game so it applies cleanly on the next resume.
  const shuffleRoles = async () => {
    if (busy || !game || game.status !== 'in_progress') return;
    if (!window.confirm(
      'Shuffle roles for all active players on both teams?\n\n' +
      'Pieces stay where they are on the board — only who controls each piece ' +
      '(including the King) changes. The game will pause so you can review the ' +
      'new rosters, then hit Resume.'
    )) return;

    setBusy(true);
    try {
      const pieces = JSON.parse(JSON.stringify(game.pieces || {}));
      const eliminated = game.eliminatedPlayers || [];

      // Permute living pieces among the active players that currently hold them.
      const shuffleTeam = (teamArr) => {
        const team = teamArr || [];
        const active = team.filter(p =>
          p.email &&
          !eliminated.includes(p.email) &&
          p.pieceId && pieces[p.pieceId] && !pieces[p.pieceId].captured
        );
        if (active.length < 2) return team; // nothing meaningful to shuffle

        const pieceIds = active.map(p => p.pieceId);
        const shuffled = derangedShuffle(pieceIds);

        const emailToNewPiece = {};
        active.forEach((p, i) => { emailToNewPiece[p.email] = shuffled[i]; });

        // Re-stamp ownership on each reassigned (living) piece.
        active.forEach((p) => {
          const newPid = emailToNewPiece[p.email];
          pieces[newPid] = {
            ...pieces[newPid],
            playerEmail: p.email,
            playerNickname: p.nickname,
          };
        });

        // Rebuild the team array, preserving order; eliminated players untouched.
        return team.map(p => {
          if (!(p.email in emailToNewPiece)) return p;
          const newPid = emailToNewPiece[p.email];
          return { ...p, pieceId: newPid, role: roleFromPiece(pieces[newPid]) };
        });
      };

      const newWhite = shuffleTeam(game.whiteTeam);
      const newBlack = shuffleTeam(game.blackTeam);

      await updateDoc(doc(db, 'games', game.id), {
        paused: true,
        whiteTeam: newWhite,
        blackTeam: newBlack,
        pieces,
        // Cancel any in-flight vote/suggestions so nothing executes under the
        // old role mapping.
        'suggestedMoves.white': [],
        'suggestedMoves.black': [],
        'activePolls.white': null,
        'activePolls.black': null,
        // Freeze human timers too; a fresh one starts on resume.
        'turnTimer.white': null,
        'turnTimer.black': null,
      });

      // Let each team know their pieces moved around.
      const notice = (team) => ({
        sender: 'SYSTEM',
        message: '🔀 Roles have been shuffled! Check your new piece — your role and vote weight may have changed.',
        timestamp: new Date().toISOString(),
        team,
      });
      await Promise.all([
        pushChat(game.id, notice('white')),
        pushChat(game.id, notice('black')),
      ]);
    } catch (err) {
      console.error('Failed to shuffle roles:', err);
    } finally {
      setBusy(false);
    }
  };

  // Build {email -> nickname} for vote lookups
  const allPlayers = [...(game.whiteTeam || []), ...(game.blackTeam || [])];
  const emailToName = {};
  for (const p of allPlayers) emailToName[p.email] = p.nickname;

  // For a team: { moveId -> [voterNames] }
  const groupVotesByMove = (team) => {
    const poll = game.activePolls?.[team];
    const suggestions = game.suggestedMoves?.[team] || [];
    if (!poll) return { groups: [], totalVoters: 0 };

    const groups = suggestions.map(s => ({
      san: s.notation,
      displayText: s.displayText,
      voters: [],
    }));
    const moveIdToIdx = {};
    suggestions.forEach((s, i) => { moveIdToIdx[s.id] = i; });

    const votes = poll.votes || {};
    Object.entries(votes).forEach(([email, moveId]) => {
      const idx = moveIdToIdx[moveId];
      if (idx === undefined) return;
      groups[idx].voters.push(emailToName[email] || email.split('@')[0]);
    });

    return { groups, totalVoters: Object.keys(votes).length };
  };

  const whiteVotes = groupVotesByMove('white');
  const blackVotes = groupVotesByMove('black');
  const isPaused = !!game.paused;

  return (
    <div className="admin-panel">
      {/* Game controls */}
      <div className="admin-controls">
        <button
          onClick={togglePause}
          disabled={busy}
          className={`admin-pause-button ${isPaused ? 'is-paused' : ''}`}
          title={isPaused ? 'Resume the game' : 'Pause the game (AI will stop)'}
        >
          {busy ? '...' : isPaused ? '▶ Resume Game' : '⏸ Pause Game'}
        </button>
        <button
          onClick={shuffleRoles}
          disabled={busy || game.status !== 'in_progress'}
          className="admin-shuffle-button"
          title="Reassign which piece each active player controls (keeps personalities, pauses the game)"
        >
          {busy ? '...' : '🔀 Shuffle Roles'}
        </button>
        {isPaused && (
          <span className="admin-paused-badge">PAUSED — AI will not move until you resume</span>
        )}
      </div>

      {/* Team Rosters */}
      <div className="admin-team-rosters">
        <div className="admin-team-roster white-roster">
          <h4>♔ White Team</h4>
          <div className="roster-list">
            {game.whiteTeam?.map((player, index) => {
              const isEliminated = game.eliminatedPlayers?.includes(player.email);
              return (
                <div key={index} className={`roster-item ${isEliminated ? 'eliminated' : ''}`}>
                  <span className="roster-role">{player.role || 'Supporter'}</span>
                  <span className="roster-name">{player.nickname}</span>
                  {isEliminated && <span className="roster-status">☠️</span>}
                </div>
              );
            })}
          </div>
        </div>
        <div className="admin-team-roster black-roster">
          <h4>♚ Black Team</h4>
          <div className="roster-list">
            {game.blackTeam?.map((player, index) => {
              const isEliminated = game.eliminatedPlayers?.includes(player.email);
              return (
                <div key={index} className={`roster-item ${isEliminated ? 'eliminated' : ''}`}>
                  <span className="roster-role">{player.role || 'Supporter'}</span>
                  <span className="roster-name">{player.nickname}</span>
                  {isEliminated && <span className="roster-status">☠️</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Current Suggestions */}
      <div className="admin-suggestions">
        <h4>Current Suggestions</h4>
        <div className="admin-suggestions-split">
          <div className="admin-suggestions-team">
            <h5>White ({game.suggestedMoves?.white?.length || 0})</h5>
            {game.suggestedMoves?.white?.length > 0 ? (
              game.suggestedMoves.white.map((s, i) => (
                <div key={i} className="admin-suggestion-item">
                  {s.displayText} - {s.player}
                </div>
              ))
            ) : (
              <p className="no-suggestions-text">No suggestions</p>
            )}
            {game.activePolls?.white && (
              <div className="admin-poll-indicator">🗳️ Poll Active</div>
            )}
          </div>
          <div className="admin-suggestions-team">
            <h5>Black ({game.suggestedMoves?.black?.length || 0})</h5>
            {game.suggestedMoves?.black?.length > 0 ? (
              game.suggestedMoves.black.map((s, i) => (
                <div key={i} className="admin-suggestion-item">
                  {s.displayText} - {s.player}
                </div>
              ))
            ) : (
              <p className="no-suggestions-text">No suggestions</p>
            )}
            {game.activePolls?.black && (
              <div className="admin-poll-indicator">🗳️ Poll Active</div>
            )}
          </div>
        </div>
      </div>

      {/* Live Vote Breakdown */}
      <div className="admin-votes">
        <h4>Live Vote Breakdown</h4>
        <div className="admin-votes-split">
          <VoteColumn label="White" team="white" data={whiteVotes} />
          <VoteColumn label="Black" team="black" data={blackVotes} />
        </div>
      </div>
    </div>
  );
};

const VoteColumn = ({ label, data }) => (
  <div className="admin-votes-team">
    <h5>{label} ({data.totalVoters} {data.totalVoters === 1 ? 'vote' : 'votes'})</h5>
    {data.groups.length === 0 ? (
      <p className="no-suggestions-text">No active poll</p>
    ) : (
      data.groups
        .slice()
        .sort((a, b) => b.voters.length - a.voters.length)
        .map((g, i) => (
          <div key={i} className="admin-vote-group">
            <div className="admin-vote-head">
              <span className="admin-vote-move">{g.displayText || g.san}</span>
              <span className="admin-vote-count">{g.voters.length}</span>
            </div>
            {g.voters.length > 0 ? (
              <div className="admin-vote-voters">{g.voters.join(', ')}</div>
            ) : (
              <div className="admin-vote-voters empty">—</div>
            )}
          </div>
        ))
    )}
  </div>
);

export default AdminPanel;
